import { ReactNode } from 'react';
import { Sun, Moon, Crown, Maximize, Minimize } from 'lucide-react';
import AppSidebar from '@/components/AppSidebar';
import UserMenu from '@/components/UserMenu';
import ImpersonateBanner from '@/components/ImpersonateBanner';
import AppBreadcrumb from '@/components/AppBreadcrumb';
import TrialBanner from '@/components/TrialBanner';
import ZClosureBanner from '@/components/ZClosureBanner';
import OfflineIndicator from '@/components/OfflineIndicator';
import { useTheme } from '@/hooks/useTheme';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useZClosureEveningToast } from '@/hooks/useZClosureEveningToast';
import { Button } from '@/components/ui/button';
import { TutorialsLink } from '@/features/tutorials';
import { MobileNavDrawer } from '@/components/shell/MobileNavDrawer';
import { useCompactLayout } from '@/hooks/useCompactLayout';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { cn } from '@/lib/utils';
import { PdaLayout, useDeviceRole } from '@/features/pda';
import { BurstProvider } from '@/features/stock-mode';

/** Appareil « PDA stock » : cadre réduit au stock ; sinon le cadre complet de l'appli. */
export default function AppLayout({ children }: { children: ReactNode }) {
  const { isPda } = useDeviceRole();
  return (
    <BurstProvider>
      {isPda ? <PdaLayout>{children}</PdaLayout> : <StandardLayout>{children}</StandardLayout>}
    </BurstProvider>
  );
}

function StandardLayout({ children }: { children: ReactNode }) {
  const { theme, toggleTheme, nextThemeLabel } = useTheme();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  // Heartbeat + poll Assistance déplacés dans ProtectedRoute pour couvrir
  // aussi les routes plein écran (PFull) — caisse, cuisine, kiosk.
  // NF525 : toast de rappel de clôture Z à partir de 22h30 si oubliée.
  useZClosureEveningToast();
  // Portrait étroit (< 600 dp) : barre latérale en tiroir, en-tête resserré.
  const { compact, isNarrow } = useCompactLayout();
  const narrow = compact && isNarrow;
  // Menu tactile : le thème passe dans le tiroir, le nom de la page garde sa place.
  const { isFeatureEnabled } = useFeatureToggles();
  const themeInDrawer = narrow && isFeatureEnabled('menu_tactile');

  return (
    <div className="flex min-h-screen w-full bg-background">
      {!narrow && <AppSidebar />}
      <div className={cn('flex flex-1 flex-col overflow-auto', narrow && 'min-w-0')}>
        <ImpersonateBanner />
        <TrialBanner />
        <div className="px-4 pt-2">
          <ZClosureBanner compact />
        </div>
        <header
          className={cn(
            'sticky top-0 z-40 flex h-14 shrink-0 items-center justify-between border-b border-border/60 bg-card/80 backdrop-blur-md',
            narrow ? 'gap-1 px-1' : 'px-6',
          )}
        >
          {narrow && <MobileNavDrawer />}
          {narrow ? (
            <div className="vp-crumb-last min-w-0 flex-1 overflow-hidden whitespace-nowrap [&_ol]:flex-nowrap">
              <AppBreadcrumb />
            </div>
          ) : (
            <AppBreadcrumb />
          )}
          <div className={cn('flex items-center', narrow ? 'shrink-0 gap-0' : 'gap-2')}>
            {/* Formation reste accessible depuis le menu ☰. */}
            {!narrow && <TutorialsLink />}
            <OfflineIndicator />
            {!narrow && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                className="h-11 w-11"
                title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn('h-11 w-11', themeInDrawer && 'hidden')}
              title={nextThemeLabel}
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : theme === 'dark' ? (
                <Crown className="h-5 w-5 text-amber-400" />
              ) : theme === 'gold' ? (
                <Crown className="h-5 w-5 text-blue-800" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
